import { h } from "../shared/safe";
import { clientWorkspaceNav } from "./workspaceNav";
import { section, webLink } from "./bits";
import type { Ctx } from "./context";

// Read-only display of what the client record says. Every value is text; the money site is a validated link; the two
// stored-JSON lists that failed to parse say so instead of quietly vanishing. Only fields the old page showed are shown:
// the record also carries private settings (budget, voice profile, podcast owner...) and those stay where they were.

export function renderFacts(ctx: Ctx): HTMLElement[] {
  const c = ctx.data.client;
  const dash = "—";
  const facts = section(
    "cd-facts",
    "Facts",
    ctx.data.warnings.length
      ? h("div", { class: "qf-cd-notice qf-cd-notice--warn", role: "status" }, h("strong", null, "Some stored details could not be read"), h("ul", { class: "qf-cd-notice__list" }, ...ctx.data.warnings.map((w) => h("li", null, w))))
      : null,
    h(
      "dl",
      { class: "deflist" },
      h("dt", null, "Category"),
      h("dd", null, c.category ?? dash),
      h("dt", null, "Address"),
      h("dd", null, c.addressLine ?? dash),
      h("dt", null, "Phone"),
      h("dd", null, c.phone ?? dash),
      h("dt", null, "Owner"),
      h("dd", null, c.owner ?? dash),
      h("dt", null, "Money site"),
      h("dd", null, webLink(c.moneySite, dash)),
      h("dt", null, "Services"),
      h("dd", null, c.services.length ? c.services.join(", ") : dash),
      h("dt", null, "Service area"),
      h("dd", null, c.serviceArea.length ? c.serviceArea.join(", ") : dash),
    ),
  );
  const claim = section(
    "cd-claim",
    "Claim block (byte-identical everywhere)",
    c.claimBlock !== null ? h("p", { class: "qf-cd-claim" }, c.claimBlock) : h("p", { class: "muted" }, "Not set yet."),
    c.attributes.length
      ? h("table", { class: "qf-cd-attrs" }, h("tbody", null, ...c.attributes.map((a) => h("tr", null, h("th", { scope: "row" }, a.label), h("td", null, a.value)))))
      : null,
  );
  return [h("div", { class: "grid cols-2 qf-cd-facts" }, facts, claim)];
}

/** Compact, task-oriented summary for Overview; full approved details remain on Business details. */
export function renderOverviewEssentials(ctx: Ctx): HTMLElement {
  const c = ctx.data.client;
  const id = encodeURIComponent(ctx.clientId);
  return section(
    "cd-facts",
    "Business essentials",
    h("dl", { class: "deflist cp-cw-deflist" },
      h("dt", null, "Official website"), h("dd", null, webLink(c.moneySite)),
      h("dt", null, "Category"), h("dd", null, c.category ?? "Not recorded"),
      h("dt", null, "Location"), h("dd", null, c.addressLine ?? "Not recorded"),
      h("dt", null, "Phone"), h("dd", null, c.phone ?? "Not recorded"),
      h("dt", null, "Services"), h("dd", null, c.services.length ? `${c.services.slice(0, 3).join(", ")}${c.services.length > 3 ? ` · +${c.services.length - 3} more` : ""}` : "None recorded"),
    ),
    ctx.data.warnings.length ? h("p", { class: "cp-cw-detail-warning", role: "status" }, "Some saved business details need attention. Open Business details to review them.", h("ul", null, ...ctx.data.warnings.map((w) => h("li", null, w)))) : null,
    h("a", { class: "cp-cw-link", href: `#/clients/${id}/facts` }, "Edit business details →"),
  );
}

export function renderHeader(ctx: Ctx): HTMLElement {
  const c = ctx.data.client;
  const id = encodeURIComponent(ctx.clientId);
  return h(
    "div", { class: "cp-client-workspace-head" },
    h("div", { class: "cp-cw-heading" },
      h("div", null,
        h("a", { class: "cp-cw-back", href: "#/clients" }, "← Clients · Switch client"),
        h("p", { class: "cp-cw-eyebrow" }, "Client workspace · Overview"),
        h("h1", null, c.name),
        h("p", { class: "cp-cw-subtitle" }, [c.category, c.addressLine].filter(Boolean).join(" · ") || c.slug),
        c.moneySite ? h("div", { class: "cp-cw-official" }, webLink(c.moneySite, "")) : null,
      ),
      h("div", { class: "cp-cw-actions" },
        h("a", { class: "cp-btn cp-btn--secondary", href: `#/clients/${id}/facts` }, "Client settings"),
        h("a", { class: "cp-btn", href: `#/clients/${id}/site` }, "＋ Create website"),
      ),
    ),
    clientWorkspaceNav(ctx.clientId, "Overview", c.name),
  );
}
